/* Vokal & Şan level definitions. */
import type { Rng } from '../../core/util';
import type { ExerciseConfig, Question } from '../exercise/types';
import type { LevelDef } from '../levels';
import { GAM, gamQ, nefesQ, sirenQ, tutQ, vAralikQ, yankiQ } from './questions';
import { rangeText, tess } from './voice';

interface VokalGroup {
  id: string;
  title: string;
  sub: string;
  total: number;
  levels: VokalLevel[];
  /** per-session context computed once (e.g. starting note of the scale ladder) */
  setup?: (lv: VokalLevel) => { base: number };
  make: (lv: VokalLevel, i: number, rng: Rng, ctx: { base: number }) => Question;
}
export interface VokalLevel extends LevelDef {
  group: VokalGroup;
}

function group(g: Omit<VokalGroup, 'levels'> & { levels: { n: number; title: string; desc: string }[] }): VokalGroup {
  const out = g as unknown as VokalGroup;
  out.levels = g.levels.map((l) => ({
    ...l,
    group: out,
    key: `vokal.egz.${g.id}.${l.n}`,
    route: `vokal.egz.${g.id}.${l.n}`,
  }));
  return out;
}

export const EX_GROUPS: VokalGroup[] = [
  group({
    id: 'tut',
    title: 'Notayı tut',
    sub: 'Duyduğun notayı söyle ve ibre ortada kalacak şekilde sabit tut.',
    total: 8,
    levels: [
      { n: 1, title: 'Kolay', desc: '±50 sent toleransla 0,8 saniye tut.' },
      { n: 2, title: 'Orta', desc: '±30 sent toleransla 1,2 saniye tut.' },
      { n: 3, title: 'Zor', desc: '±15 sent toleransla 2 saniye tut.' },
    ],
    make: (lv, _i, rng) => tutQ(lv.n, rng),
  }),
  group({
    id: 'gam',
    title: 'Gam merdiveni',
    sub: 'Kalıp önce çalar, sonra sen söylersin. Her tekrar yarım ses yükselir.',
    total: 5,
    levels: [
      { n: 1, title: "5'li gam", desc: 'Do-Re-Mi-Fa-Sol-Fa-Mi-Re-Do.' },
      { n: 2, title: 'Oktav gamı', desc: 'Bir oktav çıkıp inen gam.' },
      { n: 3, title: 'Arpej', desc: '1-3-5-8-5-3-1 akor notaları.' },
      { n: 4, title: 'Üçlü atlamalar', desc: '1-3-2-4-3-5-4-2-1 kalıbı.' },
    ],
    setup: (lv) => {
      const t = tess();
      const top = Math.max(...(GAM[lv.n]?.degs ?? [0]));
      return { base: Math.max(t.lo, Math.min(t.hi - top - 4, t.lo + 2)) };
    },
    make: (lv, i, _rng, ctx) => gamQ(lv.n, i, ctx.base),
  }),
  group({
    id: 'aralik',
    title: 'Aralık söyle',
    sub: 'Başlangıç notasından istenen aralık kadar yukarı ya da aşağı söyle.',
    total: 8,
    levels: [
      { n: 1, title: "Tam 5'li ve oktav", desc: 'Yukarı doğru en sağlam aralıklar.' },
      { n: 2, title: "3'lü, 4'lü, 5'li", desc: "Büyük 3'lü, tam 4'lü, tam 5'li yukarı." },
      { n: 3, title: "2'liler ve 3'lüler", desc: 'Yukarı ve aşağı küçük aralıklar.' },
      { n: 4, title: 'Bütün aralıklar', desc: 'Oktava kadar yukarı ve aşağı.' },
    ],
    make: (lv, _i, rng) => vAralikQ(lv.n, rng),
  }),
  group({
    id: 'yanki',
    title: 'Melodi yankısı',
    sub: 'Kısa bir melodiyi dinle ve aynısını söyle. Her nota sırayla kontrol edilir.',
    total: 8,
    levels: [
      { n: 1, title: 'Üç nota', desc: 'Yan yana notalarla küçük motifler.' },
      { n: 2, title: 'Dört nota', desc: "3'lü atlamalar da var." },
      { n: 3, title: 'Beş nota', desc: 'Daha geniş atlamalar.' },
    ],
    make: (lv, _i, rng) => yankiQ(lv.n, rng),
  }),
  group({
    id: 'siren',
    title: 'Siren takibi',
    sub: 'Ekranda kayan çizgiyi sesinle takip et. Geçiş bölgelerini yumuşatmak için ideal.',
    total: 3,
    levels: [
      { n: 1, title: 'Yavaş', desc: 'Dar aralıkta yavaş kaymalar.' },
      { n: 2, title: 'Orta', desc: 'Daha geniş ve hızlı.' },
      { n: 3, title: 'Hızlı', desc: 'Bir oktavlık hızlı kaymalar.' },
    ],
    make: (lv, _i, rng) => sirenQ(lv.n, rng),
  }),
  group({
    id: 'nefes',
    title: 'Nefes ve dayanıklılık',
    sub: 'Süreni ölç, rekorunu geliştir.',
    total: 3,
    levels: [
      { n: 1, title: 'Sabit ses', desc: 'Tek notayı olabildiğince uzun ve sabit tut. Hedef 10 saniye.' },
      { n: 2, title: '"sss" egzersizi', desc: 'Nefesi "sss" sesiyle eşit ver. Hedef 15 saniye.' },
    ],
    make: (lv) => nefesQ(lv.n),
  }),
];

export const LEVELS: VokalLevel[] = EX_GROUPS.flatMap((g) => g.levels);

export function levelConfig(level: VokalLevel): ExerciseConfig {
  const g = level.group;
  const ctx = g.setup ? g.setup(level) : { base: 0 };
  const next = g.levels[g.levels.indexOf(level) + 1];
  return {
    key: level.key,
    module: 'vokal',
    back: 'vokal.antrenman',
    title: `${g.title}\u00a0· ${level.title}`, // the dot stays with the group name when the title wraps
    levelNo: level.n,
    desc: level.desc,
    total: g.total,
    input: 'voice',
    next: next ? next.route : null,
    rules: [
      `Egzersizler senin ses aralığına göre ayarlanır: ${rangeText()}.`,
      'Kulaklık kullanırsan uygulamanın sesi mikrofona karışmaz.',
      'Zorlanırsan dur, su iç ve dinlen. Ağrı hissedersen o gün çalışmayı bırak.',
    ],
    make: (i) => g.make(level, i, Math.random, ctx),
  };
}
